import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDepartment,
  deleteDepartment,
  fetchDepartments,
  fetchEmployeePhotoUrl,
  fetchOrgMembers,
} from '../../api/org';
import { useCompanyStore } from '../../store/companyStore';
import { companyRoleLabel } from '../../types/companyRole';
import { Department, OrgSidebarFilter } from '../../types/org';

import EmployeeCardView from './EmployeeCard';
import EmployeeForm from './EmployeeForm';
import '../contract/ContractPanel.css';
import './OrgAdminPanel.css';

import { reportError } from '../../utils/notify';
type PanelView =
  | { type: 'list' }
  | { type: 'detail'; userId: number }
  | { type: 'edit'; userId: number };

const LIST_VIEW = { type: 'list' } as const;

function OrgAdminPanel() {
  const companyId = useCompanyStore((state) => state.companyId);
  const role = useCompanyStore((state) => state.role);
  const isOwner = role === 'OWNER';

  const [view, setView] = useState<PanelView>(LIST_VIEW);
  const [filter, setFilter] = useState<OrgSidebarFilter>({ type: 'all' });
  const [departments, setDepartments] = useState<Department[]>([]);
  const [members, setMembers] = useState<
    Awaited<ReturnType<typeof fetchOrgMembers>>
  >([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [newDeptName, setNewDeptName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creatingDept, setCreatingDept] = useState(false);
  const [error, setError] = useState('');
  const [thumbUrls, setThumbUrls] = useState<Record<number, string>>({});

  const loadDepartments = useCallback(async () => {
    const list = await fetchDepartments();
    setDepartments(list);
  }, []);

  const loadMembers = useCallback(
    async (keyword?: string) => {
      const params =
        filter.type === 'unassigned'
          ? { unassigned: true, q: keyword }
          : filter.type === 'department'
            ? { departmentId: filter.departmentId, q: keyword }
            : { q: keyword };
      const list = await fetchOrgMembers(params);
      setMembers(list);
    },
    [filter]
  );

  const refresh = useCallback(
    async (keyword?: string) => {
      try {
        setLoading(true);
        await Promise.all([loadDepartments(), loadMembers(keyword)]);
        setError('');
      } catch {
        reportError(setError, '조직 정보를 불러오지 못했습니다. 오너 권한을 확인해 주세요.');
      } finally {
        setLoading(false);
      }
    },
    [loadDepartments, loadMembers]
  );

  useEffect(() => {
    let cancelled = false;
    const urlsToRevoke: string[] = [];
    const loadThumbs = async () => {
      const next: Record<number, string> = {};
      await Promise.all(
        members.map(async (member) => {
          if (member.hasPhoto) {
            try {
              const url = await fetchEmployeePhotoUrl(member.userId);
              urlsToRevoke.push(url);
              next[member.userId] = url;
            } catch {
              // ignore missing photo
            }
          } else if (member.profileImageUrl) {
            next[member.userId] = member.profileImageUrl;
          }
        })
      );
      if (!cancelled) setThumbUrls(next);
    };
    void loadThumbs();
    return () => {
      cancelled = true;
      urlsToRevoke.forEach((url) => {
        if (url.startsWith('blob:')) window.URL.revokeObjectURL(url);
      });
    };
  }, [members]);

  useEffect(() => {
    setView(LIST_VIEW);
    setFilter({ type: 'all' });
    setSelectedId(null);
    setSearchKeyword('');
  }, [companyId, setView]);

  useEffect(() => {
    if (!isOwner) return;
    const timer = window.setTimeout(() => {
      void refresh(searchKeyword);
    }, view.type === 'list' ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [isOwner, filter, companyId, searchKeyword, refresh, view.type]);

  const filterLabel = useMemo(() => {
    if (filter.type === 'unassigned') return '미배치';
    if (filter.type === 'department') {
      return (
        departments.find((d) => d.departmentId === filter.departmentId)?.departmentName || '부서'
      );
    }
    return '전체 인원';
  }, [filter, departments]);

  const handleCreateDepartment = async (event: FormEvent) => {
    event.preventDefault();
    const name = newDeptName.trim();
    if (!name) return;
    try {
      setCreatingDept(true);
      await createDepartment(name);
      setNewDeptName('');
      await refresh(searchKeyword);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '부서 생성에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setCreatingDept(false);
    }
  };

  const handleDeleteDepartment = async (department: Department) => {
    const ok = window.confirm(
      `'${department.departmentName}' 부서를 삭제할까요?\n소속 인원은 미배치로 이동합니다.`
    );
    if (!ok) return;
    try {
      await deleteDepartment(department.departmentId);
      if (filter.type === 'department' && filter.departmentId === department.departmentId) {
        setFilter({ type: 'all' });
      }
      await refresh(searchKeyword);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '부서 삭제에 실패했습니다.';
      reportError(setError, message);
    }
  };

  const selectFilter = (next: OrgSidebarFilter) => {
    setFilter(next);
    setView(LIST_VIEW);
    setSelectedId(null);
  };

  const goList = () => {
    setView(LIST_VIEW);
    void refresh(searchKeyword);
  };

  if (!isOwner) {
    return (
      <main className="contract-panel">
        <div className="org-admin__denied">
          <strong>인사관리</strong>
          <p>오너만 접근할 수 있습니다.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="contract-panel org-admin">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>인사관리</strong>
          <span>부서 · 인원</span>
        </div>

        <div className="org-admin__side-nav">
          <button
            type="button"
            className={filter.type === 'all' ? 'is-active' : ''}
            onClick={() => selectFilter({ type: 'all' })}
          >
            전체 인원
          </button>
          <button
            type="button"
            className={filter.type === 'unassigned' ? 'is-active' : ''}
            onClick={() => selectFilter({ type: 'unassigned' })}
          >
            미배치
          </button>
        </div>

        <div className="org-admin__dept-block org-admin__dept-block--grow">
          <div className="org-admin__dept-head">
            <strong>부서</strong>
          </div>
          <form className="org-admin__dept-create" onSubmit={handleCreateDepartment}>
            <input
              value={newDeptName}
              onChange={(e) => setNewDeptName(e.target.value)}
              placeholder="부서명"
              maxLength={100}
            />
            <button type="submit" disabled={creatingDept}>
              추가
            </button>
          </form>
          <ul className="org-admin__dept-list org-admin__dept-list--grow">
            {departments.map((dept) => (
              <li key={dept.departmentId}>
                <button
                  type="button"
                  className={
                    filter.type === 'department' && filter.departmentId === dept.departmentId
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    selectFilter({ type: 'department', departmentId: dept.departmentId })
                  }
                >
                  <span>{dept.departmentName}</span>
                  <em>{dept.memberCount ?? 0}</em>
                </button>
                <button
                  type="button"
                  className="org-admin__dept-delete"
                  title="부서 삭제"
                  onClick={() => void handleDeleteDepartment(dept)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="contract-panel__main">
        {error && <p className="contract-panel__error">{error}</p>}

        {view.type === 'detail' ? (
          <EmployeeCardView
            userId={view.userId}
            onBack={goList}
            onEdit={() => setView({ type: 'edit', userId: view.userId })}
            onDeactivated={goList}
          />
        ) : view.type === 'edit' ? (
          <EmployeeForm
            userId={view.userId}
            departments={departments}
            onCancel={() => setView({ type: 'detail', userId: view.userId })}
            onSaved={() => setView({ type: 'detail', userId: view.userId })}
          />
        ) : (
          <div className="org-admin__main-list">
            <div className="org-admin__main-toolbar">
              <div>
                <p className="org-admin__eyebrow">인원 목록</p>
                <h2>{filterLabel}</h2>
                <p>행을 더블클릭하면 인사카드 상세로 이동합니다.</p>
              </div>
              <input
                className="org-admin__main-search"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="이름 / 사번 / 직책 / 연락처 검색"
              />
            </div>

            {loading ? (
              <p className="contract-panel__hint">불러오는 중...</p>
            ) : members.length === 0 ? (
              <div className="contract-panel__empty">
                <strong>{filterLabel}</strong>
                <p>표시할 인원이 없습니다.</p>
              </div>
            ) : (
              <div className="org-admin__table-wrap">
                <table className="org-admin__table">
                  <thead>
                    <tr>
                      <th className="is-photo">사진</th>
                      <th>이름</th>
                      <th>사번</th>
                      <th>담당</th>
                      <th>역할</th>
                      <th>부서</th>
                      <th>입사일</th>
                      <th>연락처</th>
                      <th>이메일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const name =
                        member.displayName || member.nickname || `사용자 ${member.userId}`;
                      return (
                        <tr
                          key={member.userId}
                          className={selectedId === member.userId ? 'is-active' : ''}
                          onClick={() => setSelectedId(member.userId)}
                          onDoubleClick={() =>
                            setView({ type: 'detail', userId: member.userId })
                          }
                        >
                          <td className="is-photo">
                            {thumbUrls[member.userId] ? (
                              <img src={thumbUrls[member.userId]} alt="" />
                            ) : (
                              <span className="org-admin__avatar-fallback">
                                {name.slice(0, 1)}
                              </span>
                            )}
                          </td>
                          <td>
                            <strong>{name}</strong>
                          </td>
                          <td>{member.employeeNo || '-'}</td>
                          <td>{member.jobTitle || '-'}</td>
                          <td>{companyRoleLabel(member.role)}</td>
                          <td>{member.departmentName || '미배치'}</td>
                          <td>{member.hireDate || '-'}</td>
                          <td>{member.phone || '-'}</td>
                          <td>{member.email || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

export default OrgAdminPanel;
